import { apiClient } from '../../../core/api/client';
import { ProviderProfile } from '../types';

/**
 * T090: Enhanced Provider Profile API
 * Feature: 007-provider-profile-fields
 * 
 * This API handles the enhanced provider profile endpoint that returns
 * all new fields including:
 * - Services catalog
 * - Badges
 * - Media gallery
 * - Google Places reviews
 * - Professional details (certifications, licenses, awards)
 * - Enhanced contact and operating information
 */

/**
 * Get complete provider profile with all enhanced fields
 * 
 * Endpoint: GET /api/v1/providers/:id
 * 
 * Returns comprehensive provider profile including:
 * - Hero section (tagline, media)
 * - Reviews & ratings (Google Places integration, real-time)
 * - Pricing information
 * - Operating hours and establishment year
 * - Contact details (phone, website, social media)
 * - Professional details (certifications, licenses, awards, experience)
 * - Location data (registered states)
 * - Services catalog (provider-owned services)
 * - bQuest verification badges
 * - Media gallery (photos/videos)
 * 
 * @param id - Provider UUID (ServiceProvider.id)
 * @returns Complete provider profile
 * @throws 404 if provider not found
 * @throws 503 if Google Places API unavailable (when placeId exists)
 */
export const getProviderProfile = async (
  id: string
): Promise<ProviderProfile> => {
  const response = await apiClient.get<ProviderProfile>(
    `/providers/${id}`
  );
  return response.data;
};

/**
 * Type guard helper: Check if provider has complete pricing information
 */
export const hasCompletePricing = (profile: ProviderProfile): boolean => {
  return !!(
    profile.priceRangeIndicator ||
    profile.priceDetailsText ||
    profile.services.some(s => s.servicePrice)
  );
};

/**
 * Type guard helper: Check if provider has professional credentials
 */
export const hasProfessionalCredentials = (profile: ProviderProfile): boolean => {
  return !!(
    profile.certificationsArray?.length ||
    profile.professionalLicenses?.length ||
    profile.awardsRecognitions?.length
  );
};

/**
 * Type guard helper: Check if provider has media content
 */
export const hasMediaContent = (profile: ProviderProfile): boolean => {
  return !!(
    profile.heroMediaUrl ||
    profile.media.length > 0 ||
    profile.introductionVideoUrl
  );
};

/**
 * Helper: Get primary media URL (hero or first media item)
 */
export const getPrimaryMediaUrl = (profile: ProviderProfile): string | null => {
  if (profile.heroMediaUrl) {
    return profile.heroMediaUrl;
  }
  
  const primaryMedia = profile.media.find(m => m.isPrimary);
  if (primaryMedia) {
    return primaryMedia.mediaUrl;
  }
  
  return profile.media.length > 0 ? profile.media[0].mediaUrl : null;
};

/**
 * Helper: Format price range indicator for display
 */
export const formatPriceRange = (
  indicator: ProviderProfile['priceRangeIndicator']
): string => {
  if (!indicator) return 'Price not specified';
  
  const labels: Record<string, string> = {
    '$': 'Budget-friendly',
    '$$': 'Moderate',
    '$$$': 'Premium',
    '$$$$': 'Luxury',
  };
  
  return labels[indicator] || indicator;
};

/**
 * Helper: Get display name for provider
 */
export const getProviderDisplayName = (profile: ProviderProfile): string => {
  if (profile.professionalTitle && profile.user.firstName) {
    return `${profile.user.firstName} ${profile.user.lastName || ''}, ${profile.professionalTitle}`.trim();
  }
  
  if (profile.user.firstName) {
    return `${profile.user.firstName} ${profile.user.lastName || ''}`.trim();
  }
  
  return profile.companyName;
};

